<script setup lang="ts">
/**
 * Карточка слота комбо: что именно выбрано в этой группе.
 * Кнопки «Заменить» (выбор другого товара) и «Изменить состав» (добавки/отбавки).
 */

import { computed } from 'vue';
import type { ComboGroupItem } from '@entities/product';
import { isMenuItemAvailable, menuUnavailableLabel, productVariantLabel } from '@entities/product';
import type { ComboSlot } from '@features/combo';

const props = defineProps<{
  comboSlot: ComboSlot;
  /** Слот, с которым сейчас работает пользователь */
  active?: boolean;
  /** Пользователь работает с другим слотом — этот приглушаем */
  dimmed?: boolean;
  placeholderImg?: string;
}>();
const emit = defineEmits<{
  (e: 'replace'): void;
  (e: 'customize'): void;
  (e: 'selectVariant', productId: number): void;
}>();
const isAvailable = isMenuItemAvailable;
const unavailableLabel = menuUnavailableLabel;

const product = computed(() => props.comboSlot.product);

const image = computed(() => product.value?.img?.[0]?.url || props.placeholderImg || null);

/**
 * «25 см, 320 г» — размер и граммовка выбранного товара.
 *
 * У весового блюда вместо граммовки карточки показываем ПОРЦИЮ комбо: в набор входит
 * 250 г люля-кебаба, и именно это число объясняет гостю цену слота.
 */
const meta = computed(() => {
  const parts: string[] = [];
  if (product.value?.size) parts.push(product.value.size);
  if (product.value?.portion_label) parts.push(product.value.portion_label);
  else if (product.value?.weight) parts.push(`${product.value.weight} г`);
  return parts.join(', ');
});

/**
 * Подпись выбранных добавок/отбавок — видно прямо на карточке.
 * Отбавки в меню часто уже названы «Без огурца», поэтому минус перед
 * такими названиями не ставим — иначе получается двойное отрицание.
 */
const modifiersLabel = computed(() => {
  const added = props.comboSlot.selectedAddons
    .map((id) => props.comboSlot.addons.find((a) => a.id === id)?.name)
    .filter((name): name is string => !!name);
  const removed = props.comboSlot.selectedExcludes
    .map((id) => props.comboSlot.excludes.find((e) => e.id === id)?.name)
    .filter((name): name is string => !!name)
    .map((name) => (/^без\s/i.test(name) ? name : `− ${name}`));

  return [added.length > 0 ? `+ ${added.join(', ')}` : '', ...removed]
    .filter(Boolean)
    .join(' · ');
});

const variantLabel = (variant: ComboGroupItem) => productVariantLabel(variant);
</script>

<template>
  <div
    class="combo-slot"
    :class="{ 'combo-slot--active': active, 'combo-slot--dimmed': dimmed }"
    data-testid="combo-slot"
  >
    <div class="combo-slot__body">
      <div class="combo-slot__image-wrap">
        <img v-if="image" :src="image" :alt="product?.name" class="combo-slot__image" loading="lazy" />
        <div v-else class="combo-slot__image-placeholder" />
      </div>

      <div class="combo-slot__info">
        <div class="combo-slot__group">{{ comboSlot.group.name }}</div>
        <div class="combo-slot__name">{{ product?.name ?? 'Выберите товар' }}</div>
        <div v-if="meta" class="combo-slot__meta">{{ meta }}</div>
        <p v-if="product?.description" class="combo-slot__description">{{ product.description }}</p>
        <p v-if="modifiersLabel" class="combo-slot__modifiers">{{ modifiersLabel }}</p>
      </div>
    </div>

    <!-- Размеры одноимённого товара: 25 / 30 / 35 см -->
    <div v-if="active && comboSlot.variants.length > 1" class="combo-slot__variants">
      <!-- Размер мог кончиться отдельно от блюда: «Кола 0.5» в стопе, «Кола 0.33» нет -->
      <button
        v-for="variant in comboSlot.variants"
        :key="variant.id"
        type="button"
        class="combo-slot__variant"
        :class="{
          'combo-slot__variant--selected': variant.id === product?.id && isAvailable(variant),
          'combo-slot__variant--unavailable': !isAvailable(variant),
        }"
        :aria-disabled="!isAvailable(variant)"
        :title="isAvailable(variant) ? undefined : unavailableLabel(variant)"
        @click="isAvailable(variant) && emit('selectVariant', variant.id)"
      >
        {{ variantLabel(variant) }}
      </button>
    </div>

    <div class="combo-slot__actions">
      <button
        v-if="comboSlot.families.length > 1"
        type="button"
        class="combo-slot__replace"
        data-testid="combo-slot-replace"
        @click="emit('replace')"
      >
        Заменить
      </button>
      <button
        v-if="comboSlot.canCustomize"
        type="button"
        class="combo-slot__customize"
        data-testid="combo-slot-customize"
        @click="emit('customize')"
      >
        Изменить состав
      </button>
    </div>
  </div>
</template>

<style scoped lang="scss">
.combo-slot {
  @apply rounded-2xl p-3 transition-all duration-200;
  @apply bg-gray-100/80 dark:bg-white/5;
  @apply border-2 border-transparent;

  &--active {
    @apply border-red-500 bg-red-50/60 dark:bg-white/[0.07];
  }

  &--dimmed {
    @apply opacity-40;
  }

  &__body {
    @apply flex gap-3 items-start;
  }

  &__image-wrap {
    @apply w-16 h-16 shrink-0 rounded-xl overflow-hidden bg-white/5;
  }

  &__image {
    @apply w-full h-full object-cover;
  }

  &__image-placeholder {
    @apply w-full h-full bg-gray-300 dark:bg-gray-700;
  }

  &__info {
    @apply min-w-0 flex-1;
  }

  &__group {
    @apply text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400;
  }

  &__name {
    @apply text-base font-bold text-gray-900 dark:text-white leading-tight;
  }

  &__meta {
    @apply text-xs text-gray-500 dark:text-gray-400 mt-0.5;
  }

  &__description {
    @apply text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2;
  }

  &__modifiers {
    @apply text-xs text-red-500 mt-1;
  }

  &__variants {
    @apply mt-3 grid grid-flow-col auto-cols-fr gap-1 p-1 rounded-full;
    @apply bg-gray-200/70 dark:bg-black/40;
  }

  &__variant {
    @apply py-1.5 px-2 rounded-full text-xs font-medium transition-colors;
    @apply text-gray-600 dark:text-gray-300;

    &--selected {
      @apply bg-white dark:bg-white/15 text-gray-900 dark:text-white shadow-sm;
    }

    // Размер кончился: чипс остаётся на месте (видно, что он вообще бывает),
    // но помечен и не выбирается.
    &--unavailable {
      @apply ring-1 ring-inset ring-hui-danger text-hui-danger opacity-60 cursor-not-allowed;
      @apply line-through;
    }
  }

  &__actions {
    @apply flex items-center gap-2 mt-3;
  }

  &__replace {
    @apply px-4 py-1.5 rounded-full text-sm font-semibold transition-colors;
    @apply bg-red-500/15 text-red-500 hover:bg-red-500/25;
  }

  &__customize {
    @apply px-3 py-1.5 rounded-full text-sm font-medium transition-colors;
    @apply text-gray-600 dark:text-gray-300 hover:text-red-500;
  }
}
</style>
